/** Project source uses and named relationships; collection stays connection-owned. */
import * as z from 'zod';
import { gscReportInputSchema } from './gsc-report-recipes.js';
export const PROJECT_SOURCES_GRAPH_SCHEMA_VERSION = 'integration.project_sources.v1';
export const PROJECT_SOURCES_COMMAND_SCHEMA_VERSION = 'integration.project_sources.command.v1';
export const PROJECT_SOURCE_REPORT_SCHEMA_VERSION = 'integration.project_source.report_request.v1';
export const PROJECT_SOURCE_ROLE = 'source';
export const SOURCE_RELATIONSHIPS = {
    'gsc.search_data': {
        label: 'Search data for store',
        sourceProvider: 'google-search-console',
        sourceResourceKind: 'search_console_property',
        targetProvider: 'shopify',
        targetResourceKind: 'shopify_store',
    },
};
const id = z.string().trim().min(1).max(500);
export const projectSourceScopeSchema = z.strictObject({ organizationId: id, projectId: id });
export const addProjectSourceSchema = projectSourceScopeSchema.extend({ connectionId: id });
export const removeProjectSourceSchema = projectSourceScopeSchema.extend({ sourceUseId: id });
export const saveSourceRelationshipSchema = projectSourceScopeSchema
    .extend({
    sourceConnectionId: id,
    targetConnectionId: id,
    type: z.enum(['gsc.search_data']),
    expectedRevision: z.number().int().nonnegative(),
    expectedRelationshipId: id.nullable().default(null),
})
    .refine((input) => input.sourceConnectionId !== input.targetConnectionId, {
    message: 'Source and target must differ',
})
    .refine((input) => input.expectedRevision === 0 ? input.expectedRelationshipId === null : input.expectedRelationshipId !== null, { message: 'Expected relationship identity is required for a revision update' });
export const removeSourceRelationshipSchema = projectSourceScopeSchema.extend({
    relationshipId: id,
    expectedRevision: z.number().int().positive(),
});
const membershipSchema = z.strictObject({
    id,
    projectId: id,
    connectionId: id,
    role: z.string(),
    status: z.enum(['active', 'disabled']),
    isDefault: z.boolean(),
    updatedAt: z.string(),
});
const relationshipSchema = z.strictObject({
    id,
    projectId: id,
    sourceUseId: id,
    targetUseId: id,
    sourceConnectionId: id,
    targetConnectionId: id,
    type: z.enum(['gsc.search_data']),
    status: z.enum(['active', 'disabled']),
    revision: z.number().int().positive(),
});
const canvasConnectionSchema = z.strictObject({
    id,
    provider: z.string(),
    resourceId: z.string(),
    label: z.string(),
    status: z.string(),
    connectionAvailable: z.boolean(),
});
export const projectSourcesGraphSchema = z.strictObject({
    organizationId: id,
    projects: z.array(z.strictObject({ projectId: id, name: z.string() })),
    connections: z.array(canvasConnectionSchema),
    availableConnections: z.array(canvasConnectionSchema),
    memberships: z.array(membershipSchema),
    relationships: z.array(relationshipSchema),
});
export const projectSourceReadSchema = projectSourceScopeSchema.extend({
    connectionId: id,
    relationshipId: id.optional(),
});
export const projectSourceReportSchema = projectSourceReadSchema.extend({
    relationshipId: id,
    report: gscReportInputSchema,
});
export const projectSourcesCommandSchema = z.discriminatedUnion('operation', [
    z.strictObject({ operation: z.literal('add-source'), input: addProjectSourceSchema }),
    z.strictObject({ operation: z.literal('remove-source'), input: removeProjectSourceSchema }),
    z.strictObject({ operation: z.literal('save-link'), input: saveSourceRelationshipSchema }),
    z.strictObject({ operation: z.literal('remove-link'), input: removeSourceRelationshipSchema }),
]);
const count = z.number().int().nonnegative().nullable();
export const projectSourceStatusSchema = z.strictObject({
    relationshipId: id.nullable(),
    projectId: id,
    connectionId: id,
    coverage: z.enum(['paused', 'not_started', 'collecting', 'complete']),
    latestPublishedDate: z.string().nullable().optional(),
    latestFailure: z
        .strictObject({ runId: id, code: z.string(), sourceDate: z.string().nullable(), retryable: z.boolean() })
        .nullable()
        .optional(),
    collection: z
        .strictObject({
        targetDays: z.number().nullable(),
        publishedReports: count,
        expectedReports: count,
        reportRowsStored: count,
        activeRuns: count,
        unresolvedReports: count,
        publicationPending: count,
    })
        .nullable(),
});
export function projectIntegrationsPath(projectId) {
    return `/projects/${encodeURIComponent(id.parse(projectId))}/integrations`;
}
/** Store summaries are explicit project relationships, never default-project inheritance. */
export const storeProjectSourceSchema = z
    .strictObject({
    projectId: id,
    projectName: z.string(),
    projectIntegrationsPath: z.string().startsWith('/projects/'),
    relationshipId: id,
    sourceConnectionId: id,
    property: z.string(),
    connectionAvailable: z.boolean(),
})
    .refine((row) => row.projectIntegrationsPath === projectIntegrationsPath(row.projectId), {
    message: 'Project integration link must match its project',
});
export const storeProjectSourcesSchema = z.array(storeProjectSourceSchema);
//# sourceMappingURL=project-sources.js.map